/**
 * ProductContext.jsx
 *
 * The data brain for the Tool Finder Replace path (D7) and the Results
 * page (D9). Loads the ToolFinder bundle via `loadAllData()`, derives
 * effective threads via the priority chain (refined > wizard-explicit >
 * drill-derived > null), pre-filters products per results-tab, exposes a
 * faceted filter view via `useFilters`, and intercepts thread-facet clicks
 * so they refine globally across every tab.
 *
 * Ported from Top Hammer ToolFinder v4 (see
 * `Top Hammer Toolfinder/tophammer-toolfinder-v4/src/contexts/ProductContext.jsx`,
 * 434 lines). Key Phase 4.7 mechanics ported verbatim:
 *   - `refinedThreadSystem` global filter, cleared when wizard fingerprint
 *     changes.
 *   - `drillMatchedThreads` (Path C — manufacturer + model → shanks → threads).
 *   - `effectiveThreads` priority chain.
 *   - `preFilteredProducts` per-tab with shank/coupling/pilot/drill-bits
 *     special-cases.
 *   - Intercepted `toggleFilterValue` for thread facets.
 *   - `availableDiameters` + `bitCountsByDiameter` (D5 consumer).
 *   - `tabCounts` + `visibleTabs` + auto-switch when active tab empties.
 *
 * MRT additions on top of the port (documented in the SP3 brief):
 *   1. DUAL PRODUCT LINE — TF v4 is TH-only. MRT switches between
 *      `productData` (TH) and `productDataDTH` (MRT-only field on the
 *      loadAllData envelope) based on `selections.productLine`. Same
 *      registry-driven loop, just keyed off `getTabRegistryFor()` and the
 *      DTH product map.
 *   2. WIZARD CONTEXT COUPLING — TF v4 takes `selections` as a prop; MRT
 *      reads it via `useWizard()` because the wizard state is already a
 *      context provider higher in the tree. ProductProvider therefore
 *      sits INSIDE WizardProvider in the route hierarchy.
 *
 * @module contexts/ProductContext
 * @since 2026-05-17 (Group D Sub-Plan 3, Task SP3.A.1)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { loadAllData } from '../services/dataService';
import { useFilters } from '../hooks/useFilters';
import { useWizard } from './WizardContext';
import {
  MASTER_TAB_REGISTRY,
  PLP_ATTRIBUTES,
  FACET_CONFIG,
  getTabRegistryFor,
} from '../data/wizardData';

const ProductContext = createContext(null);

export function useProducts() {
  const ctx = useContext(ProductContext);
  if (!ctx) throw new Error('useProducts must be used within <ProductProvider>');
  return ctx;
}

// ============================================================================
// THREAD FACET FIELDS — fields treated as thread selections across tabs.
// When the user clicks one of these in a facet list, we set
// `refinedThreadSystem` globally instead of toggling a per-tab filter.
// Ported verbatim from TF v4 ProductContext.jsx (THREAD_FACET_FIELDS set).
// ============================================================================
const THREAD_FACET_FIELDS = new Set([
  'thread', 'thread1', 'threadF1', 'threadF2', 'threadFemale', 'threadMale',
]);

/**
 * Check if a product's `compatibleRockDrill` field contains a given model
 * name. Handles semicolon/comma separated lists and spacing variations.
 * Ported verbatim from TF v4 ProductContext.jsx.
 */
function matchesDrillModel(fieldValue, modelName) {
  if (!fieldValue || !modelName) return false;
  const normalize = (s) => s.replace(/\s+/g, '').toUpperCase();
  const target = normalize(modelName);
  const entries = fieldValue.split(/[;,]+/).map(e => e.trim()).filter(Boolean);
  return entries.some(entry => normalize(entry) === target);
}

/**
 * ProductProvider — wraps any subtree that needs ToolFinder product data.
 * MUST sit inside <WizardProvider> because we read selections via useWizard().
 */
export function ProductProvider({ children }) {
  // MRT adaptation: TF v4 receives `selections` as a prop. We read it from
  // the wizard context that's already provided one level up.
  const { selections } = useWizard();

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  const [productData, setProductData] = useState(null);       // TH map
  const [productDataDTH, setProductDataDTH] = useState(null); // DTH map (MRT)
  const [rigsData, setRigsData] = useState(null);
  const [rockDrillsData, setRockDrillsData] = useState(null);
  const [categoryConfig, setCategoryConfig] = useState(null);
  const [rockDrillManufacturersData, setRockDrillManufacturersData] = useState(null);

  useEffect(() => {
    loadAllData().then(({
      products,
      productsDTH,
      rigs,
      rockDrills,
      categoryConfig: catConfig,
      rockDrillManufacturers,
    }) => {
      setProductData(products);
      setProductDataDTH(productsDTH);
      setRigsData(rigs);
      setRockDrillsData(rockDrills);
      setCategoryConfig(catConfig);
      setRockDrillManufacturersData(rockDrillManufacturers);
    });
  }, []);

  // ============================================================================
  // ACTIVE LINE → which dataset + tab registry to use this render.
  // MRT addition: TF v4 hardcoded TH everywhere.
  // ============================================================================
  const isDTH = selections?.productLine === 'DTH';
  const activeProductData = isDTH ? productDataDTH : productData;
  const activeTabRegistry = useMemo(
    () => getTabRegistryFor(selections?.productLine),
    [selections?.productLine],
  );

  // ============================================================================
  // ALL PRODUCTS — flattened with _category tag.
  // Ported from TF v4 (which combined TH only); MRT additionally combines
  // the DTH map so result consumers can read every product when needed.
  // ============================================================================
  const allProducts = useMemo(() => {
    const combined = [];
    const pushFrom = (map) => {
      if (!map) return;
      Object.entries(map).forEach(([category, products]) => {
        if (Array.isArray(products)) {
          products.forEach(p => combined.push({ ...p, _category: category }));
        }
      });
    };
    pushFrom(productData);
    pushFrom(productDataDTH);
    return combined;
  }, [productData, productDataDTH]);

  // ============================================================================
  // TAB STATE
  // ============================================================================
  const [activeTab, setActiveTab] = useState(activeTabRegistry[0]?.key || 'shank-adapters');

  // ============================================================================
  // PHASE 4.7: THREAD REFINEMENT
  // When the user clicks a thread facet on any tab, this becomes a global
  // filter across all tabs. Persists across tab switches. Cleared
  // explicitly via handler OR implicitly when wizard fingerprint changes.
  // ============================================================================
  const [refinedThreadSystem, setRefinedThreadSystem] = useState(null);

  // Reset refinement whenever the wizard's relevant inputs change.
  const selectionFingerprint = `${selections?.threadSystem}|${selections?.drillManufacturer}|${selections?.drillModelName}|${selections?.entryPath}|${selections?.productLine}`;
  useEffect(() => {
    setRefinedThreadSystem(null);
  }, [selectionFingerprint]);

  // ============================================================================
  // PATH C — derive threads from picked rock drill (manufacturer + model).
  // Looks up matching shank adapters and reports their threads.
  // TH-only by construction: DTH does not use shank-adapter→thread mapping.
  // ============================================================================
  const drillMatchedThreads = useMemo(() => {
    if (!productData) return null;
    const { drillManufacturer, drillModelName } = selections || {};
    if (!drillManufacturer || !drillModelName) return null;

    const shanks = productData['shank-adapters'] || [];
    const matched = shanks.filter(s =>
      s.rockDrillManufacturer === drillManufacturer &&
      matchesDrillModel(s.compatibleRockDrill, drillModelName)
    );

    if (matched.length === 0) return null;
    const threads = [...new Set(matched.map(s => s.thread).filter(Boolean))];
    return { threads, matchedShanks: matched };
  }, [productData, selections]);

  // ============================================================================
  // EFFECTIVE THREAD SYSTEM — priority chain.
  //   1. refinedThreadSystem (user clicked a thread facet)
  //   2. selections.threadSystem (wizard Path A single thread / Path B known)
  //   3. drillMatchedThreads.threads (Path C derived)
  //   4. null (no thread filter)
  // ============================================================================
  const effectiveThreads = useMemo(() => {
    if (refinedThreadSystem) return [refinedThreadSystem];
    if (selections?.threadSystem) return [selections.threadSystem];
    if (drillMatchedThreads?.threads?.length > 0) return drillMatchedThreads.threads;
    return null;
  }, [refinedThreadSystem, selections?.threadSystem, drillMatchedThreads]);

  // True when no explicit thread is locked AND multiple are derived.
  const isMultiThread = useMemo(() => {
    if (refinedThreadSystem) return false;
    if (selections?.threadSystem) return false;
    return (drillMatchedThreads?.threads?.length || 0) > 1;
  }, [refinedThreadSystem, selections?.threadSystem, drillMatchedThreads]);

  // ============================================================================
  // PRE-FILTERED PRODUCTS — keyed by tab.
  // Two modes:
  //   - Drill filter mode (TH only): shanks by mfg+model, others by derived
  //     threads.
  //   - Thread filter mode: every tab filtered by `effectiveThreads`.
  // MRT addition: iterate the active registry (TH or DTH) and the matching
  // product map.
  // ============================================================================
  const preFilteredProducts = useMemo(() => {
    if (!activeProductData) return {};

    const { threadSystem, holeDiameter, drillManufacturer, drillModelName } = selections || {};
    const hasDrillFilter = drillManufacturer && drillModelName && !threadSystem && !refinedThreadSystem;
    const result = {};

    activeTabRegistry.forEach(tab => {
      let products = [];
      tab.categories.forEach(catKey => {
        const catProducts = activeProductData[catKey];
        if (Array.isArray(catProducts)) {
          products = products.concat(catProducts);
        }
      });

      // SHANK ADAPTERS special handling — only meaningful for TH.
      if (tab.key === 'shank-adapters' && products.length > 0) {
        if (refinedThreadSystem) {
          if (drillManufacturer && drillModelName) {
            products = products.filter(p =>
              p.rockDrillManufacturer === drillManufacturer &&
              matchesDrillModel(p.compatibleRockDrill, drillModelName) &&
              p.thread === refinedThreadSystem
            );
          } else {
            products = products.filter(p => p.thread === refinedThreadSystem);
          }
        } else if (hasDrillFilter) {
          products = products.filter(p =>
            p.rockDrillManufacturer === drillManufacturer &&
            matchesDrillModel(p.compatibleRockDrill, drillModelName)
          );
        } else if (effectiveThreads && effectiveThreads.length > 0) {
          products = products.filter(p => effectiveThreads.includes(p.thread));
        }
      }
      // ALL OTHER TABS: filter by effective threads when present.
      else if (products.length > 0 && effectiveThreads && effectiveThreads.length > 0) {
        const threads = effectiveThreads;
        const threadField = tab.threadField;

        if (tab.key === 'coupling-sleeves') {
          products = products.filter(p =>
            threads.includes(p.threadF1) || threads.includes(p.threadF2)
          );
        } else if (tab.key === 'pilot-adapters') {
          products = products.filter(p =>
            threads.includes(p.threadFemale) ||
            threads.includes(p.threadMale) ||
            threads.includes(p.thread)
          );
        } else {
          products = products.filter(p => threads.includes(p[threadField]));
        }
      }

      // Drill bits: extra exact-match holeDiameter filter when set.
      if (tab.key === 'drill-bits' && holeDiameter) {
        products = products.filter(p => p.bitDiameterMm === holeDiameter);
      }

      result[tab.key] = products;
    });

    return result;
  }, [activeProductData, activeTabRegistry, selections, effectiveThreads, refinedThreadSystem]);

  // ============================================================================
  // CURRENT TAB DATA
  // ============================================================================
  const currentProducts = preFilteredProducts[activeTab] || [];
  const currentFacets = FACET_CONFIG[activeTab] || [];
  const currentAttributes = PLP_ATTRIBUTES[activeTab] || [];

  // ============================================================================
  // FACET FILTERING (via useFilters hook)
  // ============================================================================
  const {
    filteredProducts,
    facetsWithCounts,
    activeFilters,
    toggleFilterValue: rawToggleFilterValue,
    setFilters,
    clearAllFilters: rawClearAllFilters,
    activeFilterCount,
  } = useFilters(currentProducts, currentFacets);

  // ============================================================================
  // PHASE 4.7: INTERCEPTED TOGGLE — detect thread-facet clicks.
  // ============================================================================
  const toggleFilterValue = useCallback((facetId, value) => {
    const facet = currentFacets.find(f => f.id === facetId);
    const isThreadFacet = facet && THREAD_FACET_FIELDS.has(facet.field);

    if (isThreadFacet) {
      if (refinedThreadSystem === value) {
        // Toggle off — return to multi-thread view.
        setRefinedThreadSystem(null);
        rawClearAllFilters();
      } else {
        setRefinedThreadSystem(value);
        rawClearAllFilters(); // Per-tab facets cleared; thread is global now.
      }
    } else {
      rawToggleFilterValue(facetId, value);
    }
  }, [currentFacets, refinedThreadSystem, rawToggleFilterValue, rawClearAllFilters]);

  // Clear-all also drops the refined thread.
  const clearAllFilters = useCallback(() => {
    setRefinedThreadSystem(null);
    rawClearAllFilters();
  }, [rawClearAllFilters]);

  // ============================================================================
  // DIAMETER DATA — feeds wizard D5 StepHoleDiameter.
  // TH-specific (drill-bits live under TH). For DTH this returns empty;
  // DTH does not currently use the diameter snap step.
  // ============================================================================
  const availableDiameters = useMemo(() => {
    if (!productData) return [];
    const { holeDiameterMin, holeDiameterMax } = selections || {};
    let bits = productData['drill-bits'] || [];

    if (effectiveThreads && effectiveThreads.length > 0) {
      bits = bits.filter(b => effectiveThreads.includes(b.thread));
    }

    let diameters = [...new Set(bits.map(b => b.bitDiameterMm))].filter(Boolean);

    if (holeDiameterMin != null) {
      diameters = diameters.filter(d => d >= holeDiameterMin);
    }
    if (holeDiameterMax != null) {
      diameters = diameters.filter(d => d <= holeDiameterMax);
    }

    return diameters.sort((a, b) => a - b);
  }, [productData, selections, effectiveThreads]);

  const bitCountsByDiameter = useMemo(() => {
    if (!productData) return {};
    let bits = productData['drill-bits'] || [];

    if (effectiveThreads && effectiveThreads.length > 0) {
      bits = bits.filter(b => effectiveThreads.includes(b.thread));
    }

    const counts = {};
    bits.forEach(b => {
      if (b.bitDiameterMm) {
        counts[b.bitDiameterMm] = (counts[b.bitDiameterMm] || 0) + 1;
      }
    });
    return counts;
  }, [productData, effectiveThreads]);

  // ============================================================================
  // TAB COUNTS + VISIBLE TABS
  // ============================================================================
  const tabCounts = useMemo(() => {
    const counts = {};
    activeTabRegistry.forEach(tab => {
      counts[tab.key] = (preFilteredProducts[tab.key] || []).length;
    });
    return counts;
  }, [preFilteredProducts, activeTabRegistry]);

  const visibleTabs = useMemo(() => {
    return activeTabRegistry
      .filter(tab => (tabCounts[tab.key] || 0) > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tabCounts, activeTabRegistry]);

  // Keep activeTab valid: snap to the first visible tab whenever the
  // current one drops out (e.g. line switch, thread refinement).
  useEffect(() => {
    if (visibleTabs.length > 0) {
      const currentTabVisible = visibleTabs.some(t => t.key === activeTab);
      if (!currentTabVisible) {
        setActiveTab(visibleTabs[0].key);
      }
    }
  }, [visibleTabs, activeTab]);

  // ============================================================================
  // HANDLERS — Tab change + thread refinement.
  // ============================================================================
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    rawClearAllFilters(); // Drop per-tab facets only — keep refined thread.
  }, [rawClearAllFilters]);

  const handleRefineThread = useCallback((thread) => {
    setRefinedThreadSystem(thread);
    rawClearAllFilters();
  }, [rawClearAllFilters]);

  const handleClearThreadRefinement = useCallback(() => {
    setRefinedThreadSystem(null);
    rawClearAllFilters();
  }, [rawClearAllFilters]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value = useMemo(() => ({
    // Raw data
    productData,
    productDataDTH,         // MRT addition
    activeProductData,      // MRT addition — currently-selected line's map
    activeTabRegistry,      // MRT addition — TH or DTH registry
    rigsData,
    rockDrillsData,
    rockDrillManufacturersData,
    categoryConfig,
    allProducts,
    isLoading: !productData && !productDataDTH,

    // Phase 4.7: thread refinement state
    effectiveThreads,
    drillMatchedThreads,
    refinedThreadSystem,
    isMultiThread,
    handleRefineThread,
    handleClearThreadRefinement,

    // Tab state
    activeTab,
    handleTabChange,
    tabCounts,
    visibleTabs,

    // Pre-filtered
    preFilteredProducts,

    // Facet-filtered (current tab)
    filteredProducts,
    currentAttributes,
    facetsWithCounts,
    activeFilters,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount,
    setFilters,

    // Diameter data (D5 consumer)
    availableDiameters,
    bitCountsByDiameter,
  }), [
    productData, productDataDTH, activeProductData, activeTabRegistry,
    rigsData, rockDrillsData, rockDrillManufacturersData, categoryConfig,
    allProducts,
    effectiveThreads, drillMatchedThreads, refinedThreadSystem, isMultiThread,
    handleRefineThread, handleClearThreadRefinement,
    activeTab, handleTabChange, tabCounts, visibleTabs,
    preFilteredProducts,
    filteredProducts, currentAttributes, facetsWithCounts,
    activeFilters, toggleFilterValue, clearAllFilters, activeFilterCount, setFilters,
    availableDiameters, bitCountsByDiameter,
  ]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}

// Re-export the master tab registry so consumers (e.g. ResultsPage) can
// reach the TH/DTH lookup helpers without importing from wizardData
// directly. Keeps the API surface centred on ProductContext.
export { MASTER_TAB_REGISTRY };

export default ProductContext;
